import type { NodeOverlay, PageGraphSource } from './canvasTypes'
import { computePipeStates, isolationStates } from './flowAnalysis'
import { projectFlowPaths } from './flowGeometry'
import type { FlowPath } from './flowTypes'
import { projectNodeOverlays } from './graphGeometry'
import type { PageIsolationSelection } from './IsolationControllerContext'
import {
  type IsolationMarker,
  type IsolationStopBadge,
  projectIsolationMarkers,
} from './isolationMarkers'
import { selectedStops } from './isolationSelection'

export type MarkupCanvas = { width: number; height: number }

export type PageIsolationMarkup = {
  paths: FlowPath[]
  markers: IsolationMarker[]
}

export function selectedStopBadges(
  selection: PageIsolationSelection[],
  pageGraphs: PageGraphSource[]
): IsolationStopBadge[] {
  return selectedStops(selection, pageGraphs).map(
    ({ pageId, node, badge }) => ({ pageId, nodeId: node.id, badge })
  )
}

export function projectPageFlowPaths(
  pageGraphs: PageGraphSource[],
  overlays: NodeOverlay[],
  stops: IsolationStopBadge[],
  isolating: boolean
): FlowPath[] {
  const stopIds = new Set(stops.map(({ nodeId }) => nodeId))
  return pageGraphs.flatMap(({ graph }) => {
    const states = computePipeStates(graph, stopIds)
    return projectFlowPaths(
      graph.pageId,
      isolating ? isolationStates(states) : states,
      overlays
    )
  })
}

export function pageIsolationMarkup(
  page: PageGraphSource,
  selection: PageIsolationSelection[],
  canvas: MarkupCanvas
): PageIsolationMarkup {
  const overlays = projectNodeOverlays([
    {
      ...page,
      bounds: {
        denormalized_x: 0,
        denormalized_y: 0,
        denormalized_width: canvas.width,
        denormalized_height: canvas.height,
      },
    },
  ])
  const stops = selectedStopBadges(selection, [page])
  return {
    paths: projectPageFlowPaths([page], overlays, stops, true),
    markers: projectIsolationMarkers(stops, overlays, undefined),
  }
}
